"use client";

import { useMemo, useState } from 'react';
import Link from '../components/SafeLink';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';


export default function ReliabilityPage() {
  const [salary, setSalary] = useState<number>(60000);
  const [headcount, setHeadcount] = useState<number>(10);
  const [hrCost, setHrHrCost] = useState<number>(350000);
  const [fee, setFee] = useState<number>(12);

  const { inhouse, interim, eco } = useMemo(() => {
    const inhouse = salary * headcount + hrCost;
    const interim = Math.round(salary * headcount * (1 + fee / 100));
    const eco = Math.max(0, inhouse - interim);
    return { inhouse, interim, eco };
  }, [salary, headcount, hrCost, fee]);

  const formatNumber = (num: number) => num.toLocaleString('ru-RU');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    consent: false,
  });

  const [formStatus, setFormStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox'
      ? (e.target as HTMLInputElement).checked
      : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setFormStatus('Пожалуйста, введите ваше имя.');
      return;
    }
    if (!formData.phone.trim()) {
      setFormStatus('Пожалуйста, введите ваш телефон.');
      return;
    }
    if (!formData.company.trim()) {
      setFormStatus('Пожалуйста, введите название вашей компании.');
      return;
    }

    if (!formData.consent) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    setFormStatus('Отправка...');

    const { consent, ...dataToSend } = formData;
    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/reliability-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: dataToSend,
        }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', company: '', consent: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Произошла неизвестная ошибка при отправке.';
        setFormStatus(`Произошла ошибка: ${errorMessage}.`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка:', error);
    }
  };

  return (
    <main className="min-h-screen pt-16">
      <script
        id="jsonld-service"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Надёжные кадровые решения (ЧАЗ)',
            provider: { '@type': 'Organization', name: 'INTERIM' },
            areaServed: 'RU',
            serviceType: 'Temporary staffing via licensed PEA',
            termsOfService: 'https://www.interim.ru/terms',
          }),
        }}
      />

      <section
        className="relative isolate overflow-hidden text-white flex flex-col lg:flex-row items-center w-full pt-10"
        style={{
          minHeight: '49vh',
          background: 'radial-gradient(1200px 400px at 20% 0%, #2152a3 0%, #0a2a6a 60%, #071b42 100%)',
        }}
      >
        <div className="flex-1 px-4 py-8 lg:px-8 lg:py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-0 lg:pl-6">
            <span className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs">
              Направление: надёжность
            </span>
            <h1 className="mt-2 text-4xl font-extrabold leading-tight md:text-4xl">
              Надёжные кадровые решения
            </h1>
            <p className="mt-4 max-w-1xl text-white/80 text-base md:text-lg">
              Легальный ЧАЗ‑формат, операционная дисциплина и управляемые KPI. Мы берём на
              себя людей и бюрократию — вы получаете предсказуемый результат, скорость и безопасность.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]">
                <a href="#cta" className="flex items-center justify-center h-full">Получить консультацию</a>
              </Button>
              <Button asChild className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white">
                <Link href="/pricing" className="flex items-center justify-center h-full">Смотреть тарифы</Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-8 lg:px-8 lg:py-16 flex items-center lg:justify-start">
          <div className="max-w-6xl mx-auto w-full lg:mx-0">
            <div className="bg-white rounded-2xl p-4 shadow-xl w-full max-w-5xl">
              <div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-3 sm:gap-8">
                <div className="flex flex-col">
                  <div className="text-3xl font-extrabold text-[#003B9B] sm:text-4xl">100%</div>
                  <div className="text-xs text-gray-600 mt-1 sm:text-sm">комплаенс ЧАЗ</div>
                </div>
                <div className="flex flex-col">
                  <div className="text-3xl font-extrabold text-[#003B9B] sm:text-4xl">3–5 дней</div>
                  <div className="text-xs text-gray-600 mt-1 sm:text-sm">Время вывода персонала</div>
                </div>
                <div className="flex flex-col">
                  <div className="text-3xl font-extrabold text-[#003B9B] sm:text-4xl">≤72 ч</div>
                  <div className="text-xs text-gray-600 mt-1 sm:text-sm">замена кандидата</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f8f9ff] py-10 md:py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-6 md:grid-cols-3">
          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-xl font-semibold">Законно</h3>
              <ul className="mt-2 space-y-2 text-base text-black list-none">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Работа строго в рамках гл. 53.1 ТК РФ (ПП), нулевая толерантность к «заёмному труду»</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Реестр ЧАЗ, ЭДО, «Работа в России», журнал направлений, акты</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Соблюдение ограничений: ОТ, вред/опасн. условия, I–II класс</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-xl font-semibold">Прозрачно</h3>
              <ul className="mt-2 space-y-2 text-base text-black list-none">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Фиксированные SLA: время назначения, скорость заполнения, замена</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Ежемесячные отчёты: фонд оплаты труда, табели, выработка, инциденты</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Онлайн‑доступ к статусам через личный кабинет клиента</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-xl font-semibold">Предсказуемо</h3>
              <ul className="mt-2 space-y-2 text-base text-black list-none">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Кадровый резерв и резервные смены</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Двойное табелирование и контроль неявок</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>План замещений и эскалаций 24/7</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-6 md:grid-cols-2">
          <Card className="shadow-sm rounded-2xl">
            <CardContent className="p-5 md:p-7">
              <h3 className="text-xl font-semibold">Типовые кейсы</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {['Производство', 'Логистика', 'Ритейл', 'HoReCa', 'Административные функции'].map(
                  (t) => (
                    <span
                      key={t}
                      className="rounded-full border border-gray-200 bg-indigo-50 px-3 py-1 text-xs text-black">
                      {t}
                    </span>
                  ),
                )}
              </div>
              <ul className="mt-3 space-y-2 text-base text-black list-none">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Временное расширение на пик сезона (до 18 мес.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Замещение отсутствующих сотрудников</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Запуск нового участка/филиала «под ключ»</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="shadow-sm rounded-2xl">
            <CardContent className="p-5">
              <h3 className="text-xl font-semibold">Калькулятор выгоды</h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="s" className="block text-sm font-medium text-gray-700 mb-1">Оклад (₽/мес)</Label>
                  <Input
                    id="s"
                    type="number"
                    value={salary}
                    onChange={(e) => setSalary(Number(e.target.value))}
                    min="0"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="h" className="block text-sm font-medium text-gray-700 mb-1">Кол-во сотрудников</Label>
                  <Input
                    id="h"
                    type="number"
                    value={headcount}
                    onChange={(e) => setHeadcount(Number(e.target.value))}
                    min="1"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="hr" className="block text-sm font-medium text-gray-700 mb-1">Издержки на HR/админку (₽/мес)</Label>
                  <Input
                    id="hr"
                    type="number"
                    value={hrCost}
                    onChange={(e) => setHrHrCost(Number(e.target.value))}
                    min="0"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fee" className="block text-sm font-medium text-gray-700 mb-1">Комиссия INTERIM (%)</Label>
                  <Input
                    id="fee"
                    type="number"
                    value={fee}
                    onChange={(e) => setFee(Number(e.target.value))}
                    min="0"
                    max="100"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { title: "HR-отдел", value: inhouse },
                  { title: "INTERIM", value: interim },
                  { title: "Экономия", value: eco },
                ].map((item, idx) => (
                  <Card
                    key={idx}
                    className="rounded-xl border border-gray-200 p-5 min-h-[140px] shadow-md text-center flex flex-col justify-between"
                  >
                    <div className="text-gray-700 text-base font-medium">
                      {item.title}
                    </div>
                    <div className="text-gray-500 text-sm mt-1">
                      ₽/мес
                    </div>
                    <div className="text-2xl font-black text-[#003B9B] mt-3 md:text-3xl">
                      {formatNumber(item.value)}
                    </div>
                  </Card>
                ))}
              </div>

              <p className="text-gray-600 text-sm mt-2 md:text-base">
                Расчёт ориентировочный. Для точного предложения — оставьте заявку ниже.
              </p>

            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-[#f8f9ff] py-10 md:py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-6 md:grid-cols-2">
          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-xl font-semibold">Процесс за 7 шагов</h3>
              <ol className="mt-3 space-y-2 text-base text-black list-none">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Бриф и согласование профилей.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>В системе подбора персонала (ATS) осуществляем сорсинг кандидатов и скоринг для оценки их соответствия вакансии.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Интервью и проверка документов.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Инструктаж и медосмотр (при необходимости).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Трудовой договор с INTERIM + дополнительное соглашение с данными площадки.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Направление и табелирование.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                  <span>Отчёты, замены, ежемесячные KPI‑ревью.</span>
                </li>
              </ol>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border border-amber-300 rounded-2xl p-5 shadow-sm">
            <h3 className="text-l font-semibold text-black mb-2">Комплаенс и безопасность</h3>
            <ul className="space-y-2 text-custom text-black list-none">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>Ограничения ТК РФ соблюдаются; запреты — исключены</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>ОТ: вводный/повторный инструктаж, СИЗ, контроль допусков</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>НС: расследование совместно с принимающей стороной</span>
              </li>
            </ul>
          </Card>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-6 md:grid-cols-2">
          <Card className="shadow-xl rounded-2xl p-5">
            <h3 className="text-xl font-semibold mb-2">Кейс: логистический центр</h3>
            <p className="text-gray-600 text-base mb-3">
              60 сотрудников за 10 дней, снижение ЭПЗ‑издержек на 28 %, неявки &lt; 2 %, замены ≤ 48 ч.
            </p>
            <ul className="space-y-2 text-base text-black list-none">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>SLA по выработке достигнут на 104 %.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>Нулевые претензии по ОТ; все учёты в ЭДО.</span>
              </li>
            </ul>
          </Card>
          <Card className="rounded-2xl p-5">
            <h3 className="text-xl font-bold mb-6">FAQ (кратко)</h3>
            <div className="space-y-2">
              <div>
                <p className="font-bold text-left mb-1">
                  Какие договоры подписываем?
                </p>
                <p className="text-gray-600 mb-3">
                  С контрагентом — договор на оказание услуг, акты и журнал направлений; с работником — трудовой договор и дополнительное соглашение.
                </p>
              </div>
              <div>
                <p className="font-bold text-left mb-1">
                  Можно ли продлить направление?
                </p>
                <p className="text-gray-600 mb-3">
                  Да, в пределах разрешённых законом оснований и сроков; оформляется дополнительно соглашение.
                </p>
              </div>
              <div>
                <p className="font-bold text-left mb-1">
                  Какой минимальный объём?
                </p>
                <p className="text-gray-600 mb-3">
                  От одного сотрудника на 1 месяц. Массовые проекты — индивидуальное ценообразование.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section id="cta" className="bg-[#f8f9ff] py-10 md:py-14 text-gray-900">
        <div className="mx-auto max-w-6xl px-6">
          <Card className="shadow-xl rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-2">Готовы обсудить проект?</h3>
            <p className="text-gray-600 text-base mb-4">
              Оставьте контакты — менеджер свяжется в течение трех часов в рабочее время.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1">
                  <Label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Имя</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Как к вам обращаться"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Телефон</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+7 (___) ___‑__‑__"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Компания</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="ООО «Компания»"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <CustomCheckbox
                  name="consent"
                  isChecked={formData.consent}
                  onChange={handleInputChange}
                />
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  className="bg-[#FF7C22] hover:bg-orange-700 text-white rounded-xl font-bold py-3 px-5 transition-all duration-200 ease-in-out transform hover:-translate-y-0.5"
                >
                  Отправить заявку
                </button>
              </div>
            </form>
            {formStatus && (
              <p className={`mt-2 text-sm ${formStatus.startsWith('❌')
                ? 'text-red-500'
                : formStatus.startsWith('✅')
                  ? 'text-green-600'
                  : 'text-amber-600'
                }`}>
                {formStatus}
              </p>
            )}
          </Card>
        </div>
      </section>
    </main>
  );
}